import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { injectable, singleton } from 'tsyringe';

import { decrypt, encrypt } from '@/infra/adapters/cipher';
import { Response, ResultCode } from '@/infra/adapters/models';
import { deserialize } from '@/infra/adapters/transformer';
import { CONFIG } from '@/infra/config';
import { CookieKey, getCookie, setCookie } from '@/infra/cookie';
import { apiv1, MethodType, RequestOption } from '@/infra/delivers';
import { AuthenticationError, ForbiddenError } from '@/infra/exceptions';
import { delay } from '@/infra/utilities/time';
import {
    CredentialResource,
    UserInterfaceResource,
} from '@/v1/main/adapters/models';
import {
    AuthRepository,
    Credential as Cred,
    SystemType,
    UserInterface,
} from '@/v1/main/domain/auth';

@injectable()
@singleton()
export class AuthRepositoryImpl implements AuthRepository {
    private _refreshing = false;
    private _authorizationInterceptor: number = 0;
    private _refreshInterceptor: number = 0;

    public constructor() {
        console.log('new auth repository');
        this.addAuthorizationInterceptor();
        this.addRefreshInterceptor();
        console.log('add interceptor');
    }

    private onLoginRedirect() {
        this.setCredential();
        const redirect = `/${this.getSystemType()}/login`;
        if (!document.location.href.endsWith(redirect)) {
            document.location.href = CONFIG.HTTP_BASE + redirect;
        }
    }

    public addAuthorizationInterceptor(): void {
        apiv1.interceptors.request.use(
            async (config: InternalAxiosRequestConfig) => {
                config.headers['Authorization'] = await this.getAccessToken();
                return config;
            },
            (err: unknown) => {
                return Promise.reject(err);
            }
        );
    }

    public addRefreshInterceptor() {
        apiv1.interceptors.response.use(
            (response: AxiosResponse) => {
                return response;
            },
            async (err: AxiosError) => {
                const { config, cause } = err;
                if (
                    !config ||
                    config?.url?.endsWith('refresh') ||
                    (cause instanceof ForbiddenError &&
                        cause.code == ResultCode.INVALID_TOKEN) ||
                    (cause instanceof AuthenticationError &&
                        (cause.code == ResultCode.MISSING_AUTHENTICATION ||
                            cause.code == ResultCode.INVALID_AUTHENTICATION ||
                            cause.code == ResultCode.ACCESS_DENIED))
                ) {
                    // 無 API 權限或是 Token 驗證失敗
                    return this.onLoginRedirect();
                } else if (
                    config &&
                    cause instanceof AuthenticationError &&
                    cause.code == ResultCode.ACCESS_EXPIRED
                ) {
                    if (this._refreshing) {
                        await delay(1000); // 1s
                        return apiv1(config);
                    }

                    try {
                        this._refreshing = true;
                        const result = await this.refreshToken();
                        await this.setCredential(result);
                        // this._refreshing = false;
                        return apiv1(config);
                    } catch (_: unknown) {
                        // 刷新失敗
                        this.onLoginRedirect();
                    } finally {
                        this._refreshing = false;
                    }
                } else if (cause && config?.['axios-retry']?.retryCount == 0) {
                    return Promise.reject(err.cause);
                } else {
                    return Promise.reject(err);
                }
            }
        );
    }

    public getSystemType(): SystemType {
        const value = getCookie(CookieKey.SYS_TYPE);
        if (!value) {
            this.setSystemType(SystemType.WEB);
            return SystemType.WEB;
        }
        return value as SystemType;
    }
    public setSystemType(value?: SystemType): void {
        if (!value) return setCookie(CookieKey.SYS_TYPE);
        return setCookie(CookieKey.SYS_TYPE, value);
    }

    public async getAccount(): Promise<string> {
        const value = getCookie(CookieKey.ACCT);
        if (!value) return '';
        return await decrypt(value);
    }
    public async setAccount(value?: string): Promise<void> {
        if (!value) return setCookie(CookieKey.ACCT);
        const data = await encrypt(value);
        return setCookie(CookieKey.ACCT, data);
    }

    public async getAccessToken(): Promise<string> {
        const credential = this.getCredential();
        if (credential == '') {
            return '';
        }

        const data = await decrypt(credential);
        const cred = JSON.parse(data) as CredentialResource;
        return cred.tokenType + ' ' + cred.accessToken;
    }
    public async getRefreshToken(): Promise<string> {
        const credential = this.getCredential();
        if (credential == '') {
            return '';
        }
        const data = await decrypt(credential);
        const cred = JSON.parse(data) as CredentialResource;
        return cred.refreshToken;
    }
    public getCredential(): string {
        return getCookie(CookieKey.CRDL) || '';
    }
    public async setCredential(value?: Cred): Promise<void> {
        console.log('setCredential', value);
        if (!value) return setCookie(CookieKey.CRDL);
        const json = JSON.stringify(value);
        const data = await encrypt(json);
        setCookie(CookieKey.CRDL, data);
    }

    public async login(
        account: string,
        password: string
    ): Promise<Cred> | never {
        try {
            const sys_type = this.getSystemType();
            const opt: RequestOption = {
                method: MethodType.POST,
                url: '/auth/login',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                data: {
                    account,
                    password,
                    sys_type,
                },
            };

            const resp = await apiv1.request(opt);
            const data = deserialize(Response<CredentialResource>, resp.data);
            const result = deserialize(CredentialResource, data.result);
            return result;
        } catch (err: unknown) {
            console.error(err);
            throw err;
        }
    }
    public async logout(): Promise<void> | never {
        console.log('logout');
        try {
            const opt: RequestOption = {
                method: MethodType.DELETE,
                url: '/auth/logout',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            };
            await apiv1.request(opt);
        } catch (err: unknown) {
            console.error(err);
            throw err;
        }
    }
    public async refreshToken(): Promise<Cred> | never {
        try {
            const token = await this.getRefreshToken();
            const opt: RequestOption = {
                method: MethodType.POST,
                url: '/auth/refresh',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                data: {
                    token,
                },
            };
            const resp = await apiv1.request(opt);
            const data = deserialize(Response, resp.data);
            const result = deserialize(CredentialResource, data.result);
            return result;
        } catch (err: unknown) {
            console.error(err);
            throw err;
        }
    }
    public async changePassword(
        old_password: string,
        new_password: string
    ): Promise<void> | never {
        try {
            const opt: RequestOption = {
                method: MethodType.PATCH,
                url: '/auth/user/password',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                data: {
                    old_password,
                    new_password,
                },
            };
            await apiv1.request(opt);
        } catch (err: unknown) {
            console.error(err);
            throw err;
        }
    }
    public async getUserInterface(): Promise<UserInterface> | never {
        try {
            const opt: RequestOption = {
                method: MethodType.GET,
                url: '/auth/user/interface',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            };
            const resp = await apiv1.request(opt);
            const data = deserialize(Response, resp.data);
            const result = deserialize(UserInterfaceResource, data.result);
            return result;
        } catch (err: unknown) {
            console.error(err);
            throw err;
        }
    }
}
